/**
 * ScanEngine — client-side policy scanning for AgentVault.
 *
 * Evaluates scan rules against plaintext before encryption (outbound)
 * and after decryption (inbound). Pure TypeScript, no external dependencies.
 */

export interface ScanRule {
  id: string;
  name: string;
  direction: "outbound" | "inbound" | "both";
  scanner_type: "regex" | "keyword_list" | "builtin";
  scanner_config: Record<string, any>;
  action: "block" | "flag" | "warn";
  priority: number;
}

export interface ScanViolation {
  rule_id: string;
  rule_name: string;
  action: "block" | "flag" | "warn";
  scanner_type: string;
  match_summary: string; // pattern name only, never raw content
}

export interface ScanResult {
  status: "clean" | "flagged" | "blocked";
  violations: ScanViolation[];
}

// -- Builtin patterns --------------------------------------------------------

const BUILTIN_PATTERNS: Record<string, RegExp[]> = {
  pii_ssn: [/\b\d{3}-\d{2}-\d{4}\b/g, /\b\d{9}\b/g],
  pii_credit_card: [
    /\b4\d{3}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, // Visa
    /\b5[1-5]\d{2}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, // MC
    /\b3[47]\d{2}[\s-]?\d{6}[\s-]?\d{5}\b/g, // Amex
    /\b6(?:011|5\d{2})[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, // Discover
  ],
  pii_email: [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g],
  api_keys: [
    /\bsk_live_[a-zA-Z0-9]{24,}\b/g,
    /\bAKIA[0-9A-Z]{16}\b/g,
    /\bghp_[a-zA-Z0-9]{36,}\b/g,
    /\bglpat-[a-zA-Z0-9_-]{20,}\b/g,
    /\bxoxb-[0-9]+-[0-9]+-[a-zA-Z0-9]+\b/g,
  ],
  prompt_injection: [
    /\bignore\s+(?:all\s+)?(?:previous|above|prior)\s+instructions\b/gi,
    /\byou\s+are\s+now\s+(?:a|an)\s+/gi,
    /\bsystem\s*:\s*you\b/gi,
    /\bDAN\s+mode\b/gi,
    /\bdo\s+anything\s+now\b/gi,
    /\bdo\s+not\s+follow\s+any\s+(?:other\s+)?rules\b/gi,
    /\bjailbreak\b/gi,
  ],
  shell_injection: [
    /\bcurl\s+.*\|\s*(?:sh|bash|zsh)\b/gi,
    /\beval\s*\(/gi,
    /\bexec\s*\(/gi,
    /\bchmod\s+\+x\b/gi,
    /\brm\s+-rf\s+\//gi,
  ],
};

// -- ScanEngine class --------------------------------------------------------

export class ScanEngine {
  private rules: ScanRule[] = [];
  private ruleSetVersion = 0;

  loadRules(rules: ScanRule[]): void {
    this.rules = [...rules].sort((a, b) => a.priority - b.priority);
    this.ruleSetVersion = rules.length > 0 ? Math.max(...rules.map(() => 1)) : 0;
  }

  getRuleSetVersion(): number {
    return this.ruleSetVersion;
  }

  scanOutbound(plaintext: string): ScanResult {
    return this._scan(plaintext, "outbound");
  }

  scanInbound(plaintext: string): ScanResult {
    return this._scan(plaintext, "inbound");
  }

  private _scan(
    plaintext: string,
    direction: "outbound" | "inbound",
  ): ScanResult {
    const violations: ScanViolation[] = [];
    let blocked = false;
    let flagged = false;

    for (const rule of this.rules) {
      if (rule.direction !== "both" && rule.direction !== direction) {
        continue;
      }

      const matched = this._evaluateRule(rule, plaintext);
      if (!matched) continue;

      const violation: ScanViolation = {
        rule_id: rule.id,
        rule_name: rule.name,
        action: rule.action,
        scanner_type: rule.scanner_type,
        match_summary: this._buildMatchSummary(rule),
      };

      violations.push(violation);

      if (rule.action === "block") {
        blocked = true;
        break; // stop evaluating on first block
      }
      if (rule.action === "flag") {
        flagged = true;
      }
      // warn: record violation but do not change status
    }

    const status = blocked ? "blocked" : flagged ? "flagged" : "clean";
    return { status, violations };
  }

  private _evaluateRule(rule: ScanRule, text: string): boolean {
    switch (rule.scanner_type) {
      case "regex":
        return this._evalRegex(rule.scanner_config, text);
      case "keyword_list":
        return this._evalKeywordList(rule.scanner_config, text);
      case "builtin":
        return this._evalBuiltin(rule.scanner_config, text);
      default:
        return false;
    }
  }

  private _evalRegex(config: Record<string, any>, text: string): boolean {
    const pattern = config.pattern as string;
    if (!pattern) return false;
    const flags = (config.flags as string) ?? "";
    const regex = new RegExp(pattern, flags);
    return regex.test(text);
  }

  private _evalKeywordList(config: Record<string, any>, text: string): boolean {
    const words = config.words as string[];
    if (!words || words.length === 0) return false;
    const matchMode = config.match ?? "word_boundary";

    for (const word of words) {
      if (matchMode === "word_boundary") {
        const regex = new RegExp(`\\b${this._escapeRegex(word)}\\b`, "i");
        if (regex.test(text)) return true;
      } else {
        if (text.toLowerCase().includes(word.toLowerCase())) return true;
      }
    }
    return false;
  }

  private _evalBuiltin(config: Record<string, any>, text: string): boolean {
    const builtinId = config.builtin as string;

    if (builtinId === "pii_detector") {
      const categories = (config.categories as string[]) ?? [];
      for (const cat of categories) {
        const key = `pii_${cat}`;
        const patterns = BUILTIN_PATTERNS[key];
        if (patterns) {
          for (const p of patterns) {
            // Create fresh RegExp to reset lastIndex (source patterns use /g)
            const regex = new RegExp(p.source, p.flags);
            if (regex.test(text)) return true;
          }
        }
      }
      return false;
    }

    if (builtinId === "api_keys") {
      const patterns = BUILTIN_PATTERNS.api_keys;
      for (const p of patterns) {
        const regex = new RegExp(p.source, p.flags);
        if (regex.test(text)) return true;
      }
      return false;
    }

    if (builtinId === "prompt_injection") {
      const patterns = BUILTIN_PATTERNS.prompt_injection;
      for (const p of patterns) {
        const regex = new RegExp(p.source, p.flags);
        if (regex.test(text)) return true;
      }
      return false;
    }

    if (builtinId === "shell_injection") {
      const patterns = BUILTIN_PATTERNS.shell_injection;
      for (const p of patterns) {
        const regex = new RegExp(p.source, p.flags);
        if (regex.test(text)) return true;
      }
      return false;
    }

    return false;
  }

  private _buildMatchSummary(rule: ScanRule): string {
    switch (rule.scanner_type) {
      case "regex":
        return `regex:${rule.name}`;
      case "keyword_list":
        return `keyword_list:${rule.name}`;
      case "builtin": {
        const builtinId = rule.scanner_config.builtin ?? "unknown";
        return `builtin:${builtinId}`;
      }
      default:
        return rule.scanner_type;
    }
  }

  private _escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  /**
   * Scan a workspace file (e.g. SOUL.md) against all builtin patterns.
   * Runs api_keys, pii_*, prompt_injection, and shell_injection checks
   * regardless of rule direction.
   */
  /**
   * Scan a SKILL.md file for policy violations.
   * Like scanWorkspaceFile but skips prompt_injection on instruction body
   * (skills legitimately describe AI behaviors that look like injection).
   * Checks: api_keys (block), shell_injection (block), pii (flag).
   */
  static scanSkillMd(content: string): ScanResult {
    const violations: ScanViolation[] = [];
    let blocked = false;
    let flagged = false;

    const checks: Array<{ id: string; action: "block" | "flag" }> = [
      { id: "api_keys", action: "block" },
      { id: "shell_injection", action: "block" },
      { id: "pii_ssn", action: "flag" },
      { id: "pii_credit_card", action: "flag" },
      { id: "pii_email", action: "flag" },
      // Intentionally omits prompt_injection — SKILL.md instruction body
      // legitimately contains phrases like "you are now a..."
    ];

    for (const check of checks) {
      const patterns = BUILTIN_PATTERNS[check.id];
      if (!patterns) continue;

      for (const p of patterns) {
        const regex = new RegExp(p.source, p.flags);
        if (regex.test(content)) {
          violations.push({
            rule_id: `skill_${check.id}`,
            rule_name: check.id,
            action: check.action,
            scanner_type: "builtin",
            match_summary: `builtin:${check.id}`,
          });
          if (check.action === "block") blocked = true;
          if (check.action === "flag") flagged = true;
          break;
        }
      }
    }

    const status = blocked ? "blocked" : flagged ? "flagged" : "clean";
    return { status, violations };
  }

  static scanWorkspaceFile(content: string): ScanResult {
    const violations: ScanViolation[] = [];
    let blocked = false;
    let flagged = false;

    const checks: Array<{ id: string; action: "block" | "flag" }> = [
      { id: "api_keys", action: "block" },
      { id: "prompt_injection", action: "block" },
      { id: "shell_injection", action: "block" },
      { id: "pii_ssn", action: "flag" },
      { id: "pii_credit_card", action: "flag" },
      { id: "pii_email", action: "flag" },
    ];

    for (const check of checks) {
      const patterns = BUILTIN_PATTERNS[check.id];
      if (!patterns) continue;

      for (const p of patterns) {
        const regex = new RegExp(p.source, p.flags);
        if (regex.test(content)) {
          violations.push({
            rule_id: `workspace_${check.id}`,
            rule_name: check.id,
            action: check.action,
            scanner_type: "builtin",
            match_summary: `builtin:${check.id}`,
          });
          if (check.action === "block") blocked = true;
          if (check.action === "flag") flagged = true;
          break; // one match per category is enough
        }
      }
    }

    const status = blocked ? "blocked" : flagged ? "flagged" : "clean";
    return { status, violations };
  }
}
